import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { Tag, Note } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Checkbox,
  Card, CardHeader, CardTitle, CardBody,
  THead, TBody, TR, TH, TD,
} from "../../components/ui";

function FormRoles() {
  const [sectionsPermissions, setSectionsPermissions] = useState([]);
  const [roleInfo, setRoleInfo] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) getRoleToUpdate();
    else initializeSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const initializeSection = () => {
    setRoleInfo({ name: "", description: "" });
    setSectionsPermissions(
      ["users", "roles", "categories", "brands", "contacts", "products", "blogs", "media", "orders", "reviews", "pixels", "settings"].map(
        (section) => ({ section, access: { create: false, read: false, update: false, delete: false } })
      )
    );
  };

  const initializeUpdateSection = (role) => {
    setRoleInfo({ name: role.name, description: role.description });
    setSectionsPermissions(role.permissions);
  };

  const handleCheckboxChange = (section, permission) => (e) => {
    setSectionsPermissions((prev) =>
      prev.map((perm) =>
        perm.section === section ? { ...perm, access: { ...perm.access, [permission]: e.target.checked } } : perm
      )
    );
  };

  const handleSectionCheckAll = (section) => (e) => {
    const isChecked = e.target.checked;
    setSectionsPermissions((prev) =>
      prev.map((perm) =>
        perm.section === section
          ? { ...perm, access: { create: isChecked, read: isChecked, update: isChecked, delete: isChecked } }
          : perm
      )
    );
  };

  const getRoleToUpdate = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/role/get/${id}`);
      if (res?.data?.data) initializeUpdateSection(res.data.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch role");
    } finally {
      setLoading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        name: roleInfo.name.trim(),
        description: roleInfo.description.trim(),
        permissions: sectionsPermissions,
      };
      const res = id
        ? await axiosInstance.put(`/role/update/${id}`, payload)
        : await axiosInstance.post("/role/create", payload);
      toast.success(res?.data?.message || "Saved");
      if (id) getRoleToUpdate();
      else {
        initializeSection();
        navigate(-1);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title={id ? "Edit role" : "New role"}
        subtitle="Define the role and the permissions attached to it."
        breadcrumbs={[{ label: "Roles", to: "/roles" }, { label: id ? "Edit" : "New" }]}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate("/roles")}>
              Cancel
            </Button>
            <Button onClick={save} loading={loading}>
              {id ? "Save changes" : "Create role"}
            </Button>
          </>
        }
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Role details</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-5 md:grid-cols-2">
            <Input
              label="Name"
              icon={Tag}
              placeholder="e.g. Editor"
              value={roleInfo.name}
              onChange={(e) => setRoleInfo((p) => ({ ...p, name: e.target.value }))}
            />
            <Input
              label="Description"
              icon={Note}
              placeholder="What this role can do"
              value={roleInfo.description}
              onChange={(e) => setRoleInfo((p) => ({ ...p, description: e.target.value }))}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto border-t border-line">
            <table className="min-w-full text-sm">
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>Section</TH>
                  <TH className="text-center">All</TH>
                  <TH className="text-center">Read</TH>
                  <TH className="text-center">Create</TH>
                  <TH className="text-center">Update</TH>
                  <TH className="text-center">Delete</TH>
                </TR>
              </THead>
              <TBody>
                {sectionsPermissions.map((item) => {
                  const all = Object.values(item.access).every(Boolean);
                  const some = Object.values(item.access).some(Boolean);
                  return (
                    <TR key={item.section}>
                      <TD className="font-medium capitalize text-ink">{item.section}</TD>
                      <TD>
                        <div className="flex justify-center">
                          <Checkbox
                            checked={all}
                            indeterminate={some && !all}
                            onChange={(val) => handleSectionCheckAll(item.section)({ target: { checked: val } })}
                            size={16}
                          />
                        </div>
                      </TD>
                      {["read", "create", "update", "delete"].map((perm) => (
                        <TD key={perm}>
                          <div className="flex justify-center">
                            <Checkbox
                              checked={item.access[perm]}
                              onChange={(val) => handleCheckboxChange(item.section, perm)({ target: { checked: val } })}
                              size={16}
                            />
                          </div>
                        </TD>
                      ))}
                    </TR>
                  );
                })}
                {!sectionsPermissions.length && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm text-muted">
                      No sections configured.
                    </td>
                  </tr>
                )}
              </TBody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default FormRoles;
